import React, { useState } from 'react';
import { submitQueueTest } from '~/services/fund-requests';
import toast from 'react-hot-toast';
import { QueueSubmissionProcessingResult } from '~/types/fundrequests';
import ProcessingResultsModal from './ProcessingResultsModal';
import { FaFlask, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface QueueTestControlsProps {
    paymentType: string;
    refetch: () => void;
}

const QueueTestControls: React.FC<QueueTestControlsProps> = ({ paymentType, refetch }) => {
    const [testing, setTesting] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [processingResult, setProcessingResult] = useState<QueueSubmissionProcessingResult | null>(null);
    const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState<string>('');
    const [resultError, setResultError] = useState<string>('');
    const [isResultError, setIsResultError] = useState(false);

    const testScenarios = [
        { key: 'all_success', label: 'All Success', description: 'All fund requests process successfully' },
        { key: 'all_failed', label: 'All Failed', description: 'All fund requests fail to process' },
        { key: 'mixed', label: 'Mixed Results', description: 'Some succeed, some fail' },
        { key: 'validation_error', label: 'Validation Error', description: 'Validation failures for all requests' }
    ];

    const handleTestScenario = async (scenario: string) => {
        try {
            setTesting(true);
            toast.loading(`Testing ${scenario} scenario...`);

            // Use the submitQueueTest service function
            const response = await submitQueueTest(paymentType, scenario);

            toast.dismiss();
            refetch();

            // Handle response (submitQueueTest already handles the HTTP status logic)
            setProcessingResult(response.processing_result);
            setResultMessage(response.message || '');
            setResultError('');
            setIsResultError(false);
            setIsResultsModalOpen(true);

            const { successful_count, failed_count } = response.processing_result;
            if (failed_count === 0) {
                toast.success(`Test completed: All ${successful_count} requests successful`);
            } else {
                toast.success(`Test completed: ${successful_count} successful, ${failed_count} failed`);
            }

        } catch (error: any) {
            toast.dismiss();

            // Handle error case with processing result
            if (error.response?.data?.processing_result) {
                setProcessingResult(error.response.data.processing_result);
                setResultMessage('');
                setResultError(error.response.data.error || 'Test failed');
                setIsResultError(true);
                setIsResultsModalOpen(true);
                toast.error(`Test completed: ${error.response.data.processing_result.failed_count} requests failed`);
            } else {
                toast.error(`Test failed: ${error.message}`);
            }

            console.error(error);
        } finally {
            setTesting(false);
        }
    };

    const handleCloseResultsModal = () => {
        setIsResultsModalOpen(false);
        setProcessingResult(null);
        setResultMessage('');
        setResultError('');
        setIsResultError(false);
    };

    if (import.meta.env.VITE_TEST_MODE !== 'true') {
        return null;
    }

    return (
        <div className='mt-4'>
            {/* Toggle Button */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className='flex items-center gap-2 px-3 py-2 text-sm bg-yellow-100 hover:bg-yellow-200 border border-yellow-300 rounded-md text-yellow-800 transition-colors duration-200'
            >
                <FaFlask className='text-yellow-600' size={14} />
                <span className='font-medium'>Queue Response Testing</span>
                {isExpanded ? (
                    <FaChevronUp className='text-yellow-600' size={12} />
                ) : (
                    <FaChevronDown className='text-yellow-600' size={12} />
                )}
            </button>

            {/* Test Controls Panel */}
            {isExpanded && (
                <div className='mt-2 p-4 border-2 border-dashed border-yellow-300 bg-yellow-50 rounded-lg'>
                    <div className='flex items-center gap-2 mb-3'>
                        <div className='w-3 h-3 bg-yellow-400 rounded-full'></div>
                        <h3 className='text-sm font-semibold text-yellow-800'>
                            TEST MODE - Queue Response Testing ({paymentType.toUpperCase()})
                        </h3>
                    </div>

                    <p className='text-xs text-yellow-700 mb-3'>
                        Test different queue submission scenarios without calling MIP services
                    </p>

                    <div className='grid grid-cols-2 gap-2'>
                        {testScenarios.map((scenario) => (
                            <button
                                key={scenario.key}
                                onClick={() => handleTestScenario(scenario.key)}
                                disabled={testing}
                                className='px-3 py-2 text-xs bg-yellow-200 hover:bg-yellow-300 rounded border border-yellow-400 text-yellow-800 disabled:opacity-50 transition-colors duration-200'
                                title={scenario.description}
                            >
                                {testing ? 'Testing...' : scenario.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {processingResult && (
                <ProcessingResultsModal
                    isOpen={isResultsModalOpen}
                    onClose={handleCloseResultsModal}
                    processingResult={processingResult}
                    isError={isResultError}
                    message={resultMessage}
                    error={resultError}
                />
            )}
        </div>
    );
};

export default QueueTestControls; 